import { Link } from 'expo-router';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    Checkbox,
    Label,
    redactPrivate,
    XStack,
    YStack,
} from 'smoothie';

import { withHttps } from '../../../common/misc';
import {
    GoalStatus,
    GoalTaskStatus,
    type IGoal,
    type IGoalTask,
} from '../../types/Goal';
import NotifyGuide from './NotifyGuide';

interface TaskDescriptionProps {
    task: IGoalTask;
}

const TaskDescription = ({ task }: TaskDescriptionProps) => {
    const { description } = task;
    const isTaskComplete = task.status === GoalTaskStatus.COMPLETE;

    const withLineBreaks = description.replace(/\n/g, '\n');

    const labelUrlPattern = /\[([^\]]+)\]\(([^)]+)\)/; // matches [label](url) - to embed the url in the label as a hyperlink
    const urlPattern = /\b(?:https?:\/\/|www\.)\S+\b/; // matches urls - to be hyperlinked
    const boldPattern = /\*\*([^*]+)\*\*/; // matches **bold** - to be bolded
    const pattern = new RegExp(
        `${labelUrlPattern.source}|${urlPattern.source}|${boldPattern.source}`,
        'g',
    );
    const matches = withLineBreaks.match(pattern) ?? [];

    let currentIndex = 0;

    return (
        <>
            {matches.map((match: string, index: number) => {
                const startIndex = withLineBreaks.indexOf(match, currentIndex);
                const textPart = withLineBreaks.substring(
                    currentIndex,
                    startIndex,
                );
                currentIndex = startIndex + match.length;

                // Check if the match is a URL or [label](url) pattern
                const labelUrlMatch = labelUrlPattern.exec(match);
                const urlMatch = urlPattern.exec(match);
                const boldMatch = boldPattern.exec(match);

                return (
                    <BodyMediumRegular
                        key={index}
                        color={isTaskComplete ? '$gray800' : '$gray1200'}
                        textDecorationLine={
                            isTaskComplete ? 'line-through' : 'none'
                        }
                        textDecorationStyle="solid"
                    >
                        {textPart}
                        {labelUrlMatch ? (
                            <Link
                                href={
                                    withHttps(
                                        labelUrlMatch[2] ?? '',
                                    ) as `${string}:${string}`
                                }
                                target="_blank"
                                onPress={(e) => {
                                    e?.stopPropagation(); // don't mark task as complete
                                }}
                            >
                                <BodyMediumRegular
                                    textDecorationLine={
                                        isTaskComplete ? 'line-through' : 'none'
                                    }
                                    textDecorationStyle="solid"
                                    color="blue"
                                >
                                    {labelUrlMatch[1]}
                                </BodyMediumRegular>
                            </Link>
                        ) : urlMatch ? (
                            <Link
                                href={withHttps(match) as `${string}:${string}`}
                                target="_blank"
                                onPress={(e) => {
                                    e?.stopPropagation(); // don't mark task as complete
                                }}
                            >
                                <BodyMediumRegular
                                    textDecorationLine={
                                        isTaskComplete ? 'line-through' : 'none'
                                    }
                                    textDecorationStyle="solid"
                                    color="blue"
                                >
                                    {match}
                                </BodyMediumRegular>
                            </Link>
                        ) : boldMatch ? (
                            <BodyMediumEmphasized
                                key={index}
                                color={isTaskComplete ? '$gray800' : '$black'}
                                textDecorationLine={
                                    isTaskComplete ? 'line-through' : 'none'
                                }
                                textDecorationStyle="solid"
                            >
                                {boldMatch[1]}
                            </BodyMediumEmphasized>
                        ) : null}
                    </BodyMediumRegular>
                );
            })}
            <BodyMediumRegular
                color={isTaskComplete ? '$gray800' : '$gray1200'}
                textDecorationLine={isTaskComplete ? 'line-through' : 'none'}
                textDecorationStyle="solid"
                whiteSpace="pre-wrap"
            >
                {withLineBreaks.substring(currentIndex)}
            </BodyMediumRegular>
        </>
    );
};

interface TaskProps {
    task: IGoalTask;
    goalStatus: GoalStatus;
    onTaskUpdate: (taskId: string, status: GoalTaskStatus) => void;
}

const Task = ({ task, goalStatus, onTaskUpdate }: TaskProps) => {
    const [isChecked, setIsChecked] = useState(
        task.status === GoalTaskStatus.COMPLETE,
    );

    // update task if user refreshes view
    useEffect(() => {
        setIsChecked(task.status === GoalTaskStatus.COMPLETE);
    }, [task]);

    const isGoalComplete = goalStatus === GoalStatus.COMPLETE;

    const updateTask = () => {
        if (isGoalComplete) return;

        const newStatus = isChecked
            ? GoalTaskStatus.IN_PROGRESS
            : GoalTaskStatus.COMPLETE;
        setIsChecked(!isChecked);
        onTaskUpdate(task.id, newStatus);
    };

    return (
        <XStack gap={8} alignItems="flex-start">
            <Checkbox
                id={`task-${task.id}`}
                disabled={isGoalComplete}
                onPress={updateTask}
                checked={isChecked}
                tone="positive"
            />

            <Label
                htmlFor={`task-${task.id}`}
                onPress={updateTask}
                cursor="pointer"
                width="auto"
                display="inline"
                maxWidth="100%"
                paddingRight={32}
                lineHeight={22}
                {...redactPrivate()}
            >
                <TaskDescription task={task} />
            </Label>
        </XStack>
    );
};

interface GoalTasksProps {
    goal: IGoal;
    tasks: IGoalTask[];
    goalStatus: GoalStatus;
    onTaskUpdate: (taskId: string, status: GoalTaskStatus) => void;
}

const GoalTasks = ({
    goal,
    tasks,
    goalStatus,
    onTaskUpdate,
}: GoalTasksProps) => {
    const allTasksComplete = tasks.every(
        (task) => task.status === GoalTaskStatus.COMPLETE,
    );

    return (
        <YStack gap={12}>
            <BodyLargeEmphasized>Tasks</BodyLargeEmphasized>
            {goalStatus !== GoalStatus.COMPLETE && allTasksComplete && (
                <YStack width="100%" alignItems="center">
                    <NotifyGuide goal={goal} />
                </YStack>
            )}
            <YStack gap={20} paddingVertical={16}>
                {tasks.map((task) => {
                    return (
                        <Task
                            key={task.id + task.status} // id + status ensures re-render on status change
                            task={task}
                            goalStatus={goalStatus}
                            onTaskUpdate={onTaskUpdate}
                        />
                    );
                })}
            </YStack>
        </YStack>
    );
};

export default GoalTasks;
